import { useEffect } from 'react';

/**
 * @description
 * Freezes the window scroll at a fixed position while allowing
 * scroll interactions within scrollable elements.
 * fields (does not block Spacebar or arrow keys inside them).
 */

const useFreezeWindowScroll = (deps?: ReadonlyArray<unknown>): void => {
  useEffect(() => {
    const top = window.scrollY;

    const isScrollable = (ele: HTMLElement | null, target: HTMLElement | null): boolean => {
      if (!ele) {
        return false;
      }
      const isDrawerBodyScrollable = ele.scrollHeight > ele.clientHeight;
      if (isDrawerBodyScrollable) {
        return true;
      }
      if (target && ele.contains(target)) {
        const scrollable =
          (target.scrollHeight ?? 0) > target.clientHeight ||
          (target.parentElement?.scrollHeight ?? 0) > (target.parentElement?.clientHeight ?? 0);

        return scrollable;
      }
      return false;
    };
    const isEditableElement = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }
      return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    };
    const lockScroll = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) {
        return;
      }

      // Allow interaction with sliders and form controls
      if (isEditableElement(target)) {
        return;
      }
      // Allow scroll inside truly scrollable [data-allow-scroll="true"]
      const scrollableParent = target.closest('[data-allow-scroll="true"]') as HTMLElement | null;
      if (scrollableParent && isScrollable(scrollableParent, target)) {
        return;
      }

      // Block everything else
      e.preventDefault();
      window.scrollTo(0, top);
    };

    // Prevent iOS overscroll bounce in allowed areas
    const styleTag = document.createElement('style');
    styleTag.dataset.freezeScrollStyle = 'true';
    styleTag.innerHTML = `[data-allow-scroll="true"] { overscroll-behavior: contain; }`;
    document.head.appendChild(styleTag);

    window.addEventListener('wheel', lockScroll, { passive: false });
    window.addEventListener('touchmove', lockScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', lockScroll);
      window.removeEventListener('touchmove', lockScroll);
      styleTag.remove();
    };
  }, deps);
};

export default useFreezeWindowScroll;
